<app-spinner-overlay
    [showSpinner]="isSaving"
>
    <div
        *ngIf="formError"
        class="alert alert-danger"
        role="alert"
        data-test="deposit-form-error"
    >
        <div class="hstack gap-2 justify-content-between">
            <div>{{ formError }}</div>
            <button
                type="button"
                class="btn-close m-0"
                (click)="clearFormErrors()"
            >
            </button>
        </div>
    </div>
    <div
        *ngIf="bookingSlotUnavailableWarning"
        class="alert alert-warning"
        role="alert"
        data-test="booking-slot-unavailable-warning"
    >
        <div class="hstack gap-2 justify-content-between">
            <div>{{ bookingSlotUnavailableWarning }}</div>
            <button
                type="button"
                class="btn-close m-0"
                (click)="clearFormErrors()"
            >
            </button>
        </div>
    </div>
    <form
        appVenueColour
        [primaryColour]="venue?.primaryColour"
        [ngClass]="{'Ryft--payform': isRyftPayment}"
        class="h-100 vstack gap-3"
    >
        <div class="vstack gap-2">
            <h5
                *ngIf="showTitle"
            >
                {{ paymentTerminology.label }}
            </h5>
            <div class="vstack gap-2">
                <div class="vstack gap-2 rounded p-3 bg-light flex-grow-0">
                    @if (bookingDetails?.hasUnpaidOrder) {
                        <section class="vstack gap-3" data-testid="pending-order-payment-summary" aria-label="Product payment and refund terms">
                            <span class="fw-semibold">Pay for your products</span>
                            @for (line of bookingDetails?.sale?.order?.lineItems; track line.id) {
                                <div class="d-flex justify-content-between gap-3">
                                    <span>{{ line.quantity }} × {{ line.name }}
                                        @for (modifier of line.modifiers; track $index) {
                                            <small class="d-block text-secondary">{{ modifier.name }}</small>
                                        }
                                        @if (line.paidOnline) { <app-booking-product-refund-policy [policy]="line" /> }
                                    </span>
                                    @if (line.paidOnline) { <strong>{{ null | currencyUnit:line.unitAmount() * line.quantity:bookingDetails?.sale?.order?.currencyCode }}</strong> }
                                </div>
                            }
                            @if ((bookingDetails?.depositAmountOutstanding ?? 0) > 0) {
                                <div class="d-flex justify-content-between border-top pt-2">
                                    <span>Booking deposit</span>
                                    <strong>{{ null | currencyUnit:(bookingDetails?.depositAmountOutstanding ?? 0):bookingDetails?.depositCurrencyCode }}</strong>
                                </div>
                                @if (bookingDetails?.depositRefundPolicy) {
                                    <div><span class="small fw-semibold">Deposit refund policy</span><quill-view [content]="bookingDetails?.depositRefundPolicy" /></div>
                                } @else if (refundCutOffTime) {
                                    <small>The deposit is refundable if you cancel before {{ refundCutOffTime | date:'short' }}.</small>
                                } @else {
                                    <small>The booking deposit is non-refundable.</small>
                                }
                            }
                            <div class="d-flex justify-content-between border-top pt-2 fw-semibold"><span>Total to pay</span><span>{{ amount.toFormat() }}</span></div>
                        </section>
                    } @else {
                    <span class="badge bg-dark-subtle text-dark-emphasis lh-sm me-auto fw-semibold">
                        {{ paymentTerminology.label }} Required
                    </span>
                    <label class="text-muted">
                        We require a {{ paymentTerminology.lowercaseLabel }} of
                        <span class="fw-bold">
                            {{ amount.toFormat() }}
                        </span>
                        to book your table.
                        <br/>
                        @if (venue?.depositRefundPolicy) {
                            <div class="quill-flush">
                                <quill-view
                                    [content]="venue?.depositRefundPolicy"
                                >
                                </quill-view>
                            </div>
                        } @else {
                            @if (refundCutOffTime) {
                                <span>
                                    Cancellations or no-shows after
                                    {{ refundCutOffTime | date:'d MMM y, h:mm a' }}
                                    will not be refunded.
                                </span>
                            } @else if (refundCutOffHours) {
                                <span>
                                    Cancellations or no-shows with less than
                                    {{ refundCutOffHours | i18nPlural : hoursPluralMapping }}
                                    notice will not be refunded.
                                </span>
                            } @else {
                                <span>
                                    Cancellations or no-shows will not be refunded.
                                </span>
                            }
                        }
                    </label>
                    }
                </div>
                <div *ngIf="shouldCollectCustomerEmail">
                    <label
                        for="deposit-customer-email"
                        class="form-label"
                    >
                        Email address
                    </label>
                    <input
                        id="deposit-customer-email"
                        type="email"
                        autocomplete="email"
                        class="form-control"
                        data-test="deposit-customer-email"
                        [formControl]="billingEmailAddressControl"
                        [attr.aria-invalid]="billingEmailAddressControl.invalid && billingEmailAddressControl.touched"
                        [attr.aria-describedby]="billingEmailAddressControl.invalid && billingEmailAddressControl.touched
                            ? 'deposit-customer-email-error'
                            : null"
                        [class.is-invalid]="billingEmailAddressControl.invalid && billingEmailAddressControl.touched"
                    />
                    <div
                        id="deposit-customer-email-error"
                        class="invalid-feedback"
                    >
                        Please enter a valid email address.
                    </div>
                </div>
                <div
                    id="payment-apple-wallet-button"
                    [hidden]="!walletPaymentAvailable"
                >
                </div>
                <div
                    id="payment-google-wallet-button"
                    [hidden]="!walletPaymentAvailable"
                >
                </div>
                <div
                    class="position-relative vstack justify-content-start justify-content-sm-center"
                    style="min-height: 150px; transition: none;"
                >
                    <div
                        id="payment-card-form"
                        [class.payment-card-loading]="!paymentFormIsReady"
                    >
                    </div>
                    <div
                        id="payment-card-form-spinner"
                        [class.d-none]="!!formError"
                        class="align-self-center position-absolute"
                        style="margin-top: -10px;"
                    >
                        <div
                            class="spinner-border"
                            role="status"
                        >
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
                <div
                    *ngIf="isRyftPayment"
                    class="small text-muted"
                >
                    Enter your card details to {{ paymentTerminology.actionPhrase }}.
                </div>
            </div>
        </div>
        <div class="vstack justify-content-end">
            <div class="hstack gap-2">
                <button
                    *ngIf="showBackButton"
                    type="button"
                    class="btn btn-secondary w-auto"
                    (click)="onBackSelected()"
                >
                    Back
                </button>
                <div
                    *ngIf="showBackButton"
                    class="flex-grow-1 ms-auto me-auto"
                >
                </div>
                <button
                    type="button"
                    class="btn btn-primary"
                    [ngClass]="showBackButton ? 'w-auto' : 'flex-grow-1'"
                    [disabled]="isSaving || !paymentFormIsReady"
                    (click)="onBookSelected()"
                >
                    Continue
                </button>
            </div>
        </div>
    </form>
</app-spinner-overlay>
